import { useLoaderData, useSearchParams, Link, useNavigation, useLocation } from '@remix-run/react';
import { useEffect, useState, useRef } from 'react';
import type { MetaFunction, LoaderFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import UniversalProductGrid from '~/components/universal-product-grid';
import UniversalFilterMenu, { UniversalFilterMenuConfig } from '~/components/universal-filter-menu';
import { getShopFilteringData, type ShopFilteringData } from '~/api/shop-filtering.server';
import { getSelectedStore } from '~/cookies';
import { authFromRequest } from '~/utils/session.server';
import { useFocusTrap } from '~/hooks/use-focus-trap';
import { useScrollToProducts } from '~/hooks/use-scroll-to-products';
import WelcomeSection from './components/welcome-section';
import FeaturedBrandsHome from './components/featured-brands-home';
import CatalystRewards from './components/catalyst-rewards';
import AboutPreview from './components/about-preview';
import PromotionalCarousel from '~/components/promotional-carousel';
import { fetchCarousels } from '~/api/promotional-carousel.server';
import { generateOrganizationSchema } from '~/utils/schema-utils';
import { socialImageMeta } from '~/utils/social-meta';

export const meta: MetaFunction = () => {
  // Force deployment after revert
  return [
    { title: 'California Cannabis Dispensary Menu - Catalyst Cannabis Dispensaries in CA' },
    {
      name: 'description',
      content:
        'Buy Top Cannabis Products & Brands at Catalyst Cannabis Dispensaries around California, with the best Flower, Pre-Rolls, Vapes, Edibles and more!',
    },
    { tagName: 'link', rel: 'canonical', href: 'https://catalyst-cannabis.com' },
    {
      name: 'robots',
      content: 'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large',
    },
    { property: 'og:locale', content: 'en_US' },
    { property: 'og:type', content: 'website' },
    {
      property: 'og:title',
      content: 'California Cannabis Dispensary Menu - Catalyst Cannabis Dispensaries in CA',
    },
    {
      property: 'og:description',
      content:
        'Buy Top Cannabis Products & Brands at Catalyst Cannabis Dispensaries around California, with the best Flower, Pre-Rolls, Vapes, Edibles and more!',
    },
    { property: 'og:url', content: 'https://catalyst-cannabis.com/' },
    { property: 'og:site_name', content: 'Catalyst Cannabis Dispensaries' },
    ...socialImageMeta({ alt: 'Catalyst Cannabis Dispensaries' }),
    { name: 'twitter:card', content: 'summary_large_image' },
    {
      name: 'twitter:title',
      content: 'California Cannabis Dispensary Menu - Catalyst Cannabis Dispensaries in CA',
    },
    {
      name: 'twitter:description',
      content:
        'Buy Top Cannabis Products & Brands at Catalyst Cannabis Dispensaries around California, with the best Flower, Pre-Rolls, Vapes, Edibles and more!',
    },
    { name: 'twitter:site', content: '@https://x.com/Catalyst_WFTP' },
    { name: 'twitter:creator', content: '@https://x.com/Catalyst_WFTP' },
    { name: 'twitter:label2', content: 'Time to read' },
    { name: 'twitter:data2', content: 'Less than a minute' },
  ];
};

// Enhanced homepage loader with universal filtering support
export interface HomepageLoaderData extends ShopFilteringData {
  carousels: Record<string, any>;
}

export const loader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);
  const store = (await getSelectedStore(request)) || 'catalystbellflower';

  // Parse URL parameters for filtering (same as shop page)
  const { getSanitizedParam } = await import('~/utils/search-params-helpers');

  const filters: Record<string, string> = {};
  const paramMapping: Record<string, string> = {
    category: 'category_type', // Map 'category' URL param to 'category_type' filter
    brand: 'brand',
    subtype: 'subtype',
    classification: 'classification',
    size: 'size',
  };

  Object.entries(paramMapping).forEach(([key, param]) => {
    const value = getSanitizedParam(url.searchParams, key);
    if (value) filters[param] = value;
  });

  // 🎯 DEALS HANDLING: Map deals URL parameter to onsale filter
  const deals = getSanitizedParam(url.searchParams, 'deals') === 'true';
  if (deals) {
    filters.onsale = 'true';
  }

  const sort = url.searchParams.get('sort') || 'Sort by Popular';
  const search = url.searchParams.get('search') || '';
  const page = parseInt(url.searchParams.get('page') || '1', 10);

  try {
    // Fetch homepage filtering data and brand carousel in parallel
    // 🛡️ ERROR HANDLING: Brand carousel failures won't break the page
    const [filteringData, carouselsResult] = await Promise.allSettled([
      getShopFilteringData(store || '', filters, sort, search, page, authFromRequest(request)),
      fetchCarousels(['slot_a1w6gj', 'slot_j47o52'], store || '').catch(() => ({})),
    ]);

    // Extract results with fallbacks
    const filteringDataResult = filteringData.status === 'fulfilled' ? filteringData.value : null;
    const carousels =
      carouselsResult.status === 'fulfilled' ? carouselsResult.value : {};

    // If main filtering data failed, return error state
    if (!filteringDataResult) {
      console.error('❌ Homepage filtering data failed to load');
      return json({
        products: [],
        total: 0,
        facets: {},
        activeFacetValuesByType: {},
        carousels: carousels || {},
      });
    }

    return json({
      ...filteringDataResult,
      carousels: carousels || {},
    });
  } catch (error) {
    console.error('❌ Homepage loader error:', error);
    // Return empty data instead of crashing - allows page to render gracefully
    return json({
      products: [],
      total: 0,
      facets: {},
      activeFacetValuesByType: {},
      brandCarouselData: [],
      carousels: {},
    });
  }
};

export default function ShopSection() {
  const { products, baseFacets, contextualFacets, total, dealsAvailable, carousels } =
    useLoaderData<HomepageLoaderData>();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  const scrollToProductsRef = useScrollToProducts();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const [allProducts, setAllProducts] = useState<any[]>(products);
  const navigation = useNavigation();
  const location = useLocation();

  // Focus trap for mobile drawer
  const mobileDrawerTrapRef = useFocusTrap(mobileFilterOpen);

  // Debug brand carousel data

  // Mobile filter toggle handler
  const toggleMobileFilter = () => {
    setMobileFilterOpen(!mobileFilterOpen);
  };

  // Close mobile filter on window resize (desktop)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileFilterOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile filter on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && mobileFilterOpen) {
        setMobileFilterOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileFilterOpen]);

  // Universal filter menu configuration for homepage (matches Embarc homepage)
  const filterConfig: UniversalFilterMenuConfig = {
    pageType: 'homepage',
    visibleFilters: {
      category_type: true, // Show category filter (multi-select, works with brands)
      brand: true,
      subtype: true,
      classification: true,
      size: true,
      discount_applied: true,
    },
    navigation: {
      categoryNavigation: false, // Filter on homepage - do not navigate
      brandNavigation: false, // Filter on homepage - do not navigate
    },
    labels: {
      category_type: 'Category',
    },
    defaultOpenSections: ['category_type'], // Category open by default on homepage
  };

  // Universal product grid configuration for homepage
  const productGridConfig = {
    pageType: 'homepage' as const,
    title: searchTerm ? `Search Results for "${searchTerm}"` : 'Shop All Products',
    showMobileFilterToggle: true,
  };

  // ⚡ CRITICAL: Clear filters when navigating to a new page (matches Embarc pattern)
  // This ensures filters don't persist when switching between pages
  const previousPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    // Skip on initial mount (previousPathnameRef.current is null)
    if (previousPathnameRef.current === null) {
      previousPathnameRef.current = location.pathname;
      return;
    }

    // Only clear if pathname actually changed (navigating to a different page)
    if (previousPathnameRef.current !== location.pathname) {
      // Check if we have filter params to clear
      const hasFilterParams = Array.from(searchParams.keys()).some(
        (key) => key !== 'page' && key !== 'sort' && key !== 'search',
      );

      // If we have filter params, clear them (preserve page, sort, search)
      if (hasFilterParams) {
        const newParams = new URLSearchParams();
        // Preserve non-filter params (page, sort, search)
        if (searchParams.get('page')) newParams.set('page', searchParams.get('page')!);
        if (searchParams.get('sort')) newParams.set('sort', searchParams.get('sort')!);
        if (searchParams.get('search')) newParams.set('search', searchParams.get('search')!);
        setSearchParams(newParams, { replace: true });
      }

      // Update ref to current pathname
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname, searchParams, setSearchParams]); // Only run when pathname changes

  // ⚡ CRITICAL: Immediately reset products when pathname changes (new page navigation)
  useEffect(() => {
    setAllProducts(products);
  }, [location.pathname, products]);

  // ⚡ PERFORMANCE: Only set loading for filter changes, not page navigation
  useEffect(() => {
    // If navigating to a new page, don't set loading - Remix handles that
    if (navigation.location && navigation.location.pathname !== location.pathname) {
      return; // Navigating to new page - skip loading
    }

    // Only set loading if we're filtering on the same page
    if (navigation.state === 'loading') {
      setIsFiltersLoading(true);
    }
  }, [navigation.state, navigation.location, location.pathname]);

  // Reset accumulated products and loading state when filters change
  useEffect(() => {
    // Only reset when navigation is idle (loader has finished) to avoid resetting to stale data
    if (navigation.state === 'idle') {
      setAllProducts(products);
      setIsFiltersLoading(false);
    }
  }, [
    products,
    total, // Track total changes to detect when results update
    navigation.state, // Only reset when navigation is idle (loader finished)
  ]);

  // On mount: if the URL includes ?open-filters=1, trigger mobile filter and remove the parameter.
  useEffect(() => {
    if (searchParams.get('open-filters') === '1') {
      setMobileFilterOpen(true);
      // Remove the open-filters query parameter.
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete('open-filters');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Custom header for search results
  const renderSearchHeader = () => {
    if (!searchTerm) return null;

    return (
      <div className="mx-auto max-w-[105rem] p-4">
        <h1 className="font-chaletNewYork flex items-center text-[22px] text-black md:text-2xl lg:text-3xl">
          <span className="mr-2">🔍</span>
          Search for "{searchTerm}" &nbsp;
          <Link to="/shop">
            <img className="h-5 w-5" src="/assets/icons/close-icon.svg" alt="close icon" decoding="async" />
          </Link>
        </h1>
      </div>
    );
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateOrganizationSchema()),
        }}
      />
      {/* 420 Promotional Carousel — full width, live 4/19-4/20 only */}
      <PromotionalCarousel data={carousels?.['slot_a1w6gj']} />

      {/* April 2026 Promotional Carousel */}
      <PromotionalCarousel data={carousels?.['slot_j47o52']} contained />

      {renderSearchHeader()}
       <div className="md:hidden">
        <WelcomeSection />
      </div>

      {/* Category Menu - positioned below carousel, consistent with other pages */}
      {/* CategoryMenu temporarily disabled - no space needed */}
      {/* <div className="min-h-[80px]">
          <div className="mx-auto max-w-[105rem] px-3 lg:px-8">
            <CategoryMenu />
          </div>
        </div> */}

      {/* Mobile Filter Overlay */}
      {mobileFilterOpen && (
        <div
          className="fixed inset-0 z-[99998] bg-gray-700/60 lg:hidden"
          onClick={toggleMobileFilter}
        />
      )}

      {/* Mobile Filter Sidebar - matches other pages pattern */}
      <div
        ref={mobileDrawerTrapRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filter products"
        tabIndex={-1}
        className={`font-futura fixed top-0 left-0 z-[99999] h-[100dvh] w-full transform bg-white shadow-xl transition-transform lg:hidden ${
          mobileFilterOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)' }}
      >
        <div className="relative flex h-full flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <UniversalFilterMenu
              config={filterConfig}
              contextualFacets={contextualFacets}
              baseFacets={baseFacets}
              total={total}
              dealsAvailable={dealsAvailable || 0}
              trapFocus={mobileFilterOpen}
              instanceId="mobile"
              onRequestClose={() => setMobileFilterOpen(false)}
              isLoading={isFiltersLoading}
              onLoadingChange={setIsFiltersLoading}
            />
          </div>
        </div>
      </div>

      {/* Main Content Area - matches shop page layout */}
      <section id="homepage-products-page" ref={scrollToProductsRef}>
        <div className="mx-auto max-w-[105rem] py-2 lg:px-8 lg:py-5">
          <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            {/* Left Column - Desktop Filter Menu */}
            <div className="desktop-sticky-filters hidden lg:block">
              <UniversalFilterMenu
                config={filterConfig}
                contextualFacets={contextualFacets}
                baseFacets={baseFacets}
                total={total}
                dealsAvailable={dealsAvailable || 0}
                instanceId="desktop"
                isLoading={isFiltersLoading}
                onLoadingChange={setIsFiltersLoading}
              />
            </div>

            {/* Right Column - Products Grid */}
            {/* Pass products directly like Embarc - no ProductProvider needed */}
            <div className="flex-1">
              <UniversalProductGrid
                config={productGridConfig}
                onMobileFilterToggle={toggleMobileFilter}
                total={total}
                products={allProducts}
                isLoading={isFiltersLoading}
              />
            </div>
          </div>
        </div>
      </section>
      <FeaturedBrandsHome />
      <CatalystRewards />
      <AboutPreview />
    </>
  );
}
